import { REMINDERS_PAUSED_REASONS } from '@shared/core/billing'
import { FreePlanStatusCard } from 'src/modules/payment/components/free-plan-status-card'
import { PaymentPausedStatusCard } from 'src/modules/payment/components/payment-paused-status-card'
import { PlanOfferCard } from 'src/modules/payment/components/plan-offer-card'
import { PlansGuaranteeBand } from 'src/modules/payment/components/plans-guarantee-band'
import { PlansIntervalToggle } from 'src/modules/payment/components/plans-interval-toggle'
import { PlansValueRow } from 'src/modules/payment/components/plans-value-row'
import { usePaddlePlanData } from 'src/modules/payment/hooks/use-paddle-plan-data'
import { getPaddlePriceCurrencyLabel, getPlanButtonLabelId } from 'src/modules/payment/utils/plans-offer.utils'
import type { PlansContentProps } from './plans-content.interface'
import classes from './plans-content.module.scss'

export function PlansContent(props: PlansContentProps) {
  const { plans, isYearly, onIntervalChange } = props
  const firstPlanId = plans[0]?.paddle_id
  const firstPlanPrice = usePaddlePlanData({ planId: firstPlanId }, { enabled: firstPlanId != null })

  return (
    <div className={classes.root}>
      <AccountStatus {...props} />
      <PlansIntervalToggle
        isYearly={isYearly}
        onChange={onIntervalChange}
        currencyLabel={getPaddlePriceCurrencyLabel(firstPlanPrice.data)}
      />
      <PlanCards {...props} />
      <PlansGuaranteeBand />
    </div>
  )
}

function AccountStatus({ account, currentPlan, ...props }: PlansContentProps) {
  if (account.isFreeTrial) {
    return (
      <>
        <FreePlanStatusCard
          sentCount={props.sentCount}
          limit={props.freeRemindersLimit}
          isPaused={account.isPaused}
          isRunningLow={account.isRunningLow}
          notSentCount={props.notSentCount}
          reason={account.pausedReason}
          endedAt={props.subscriptionEndedAt}
        />
        <PlansValueRow />
      </>
    )
  }

  if (account.pausedReason === REMINDERS_PAUSED_REASONS.paymentPaused && currentPlan) {
    return (
      <PaymentPausedStatusCard
        planName={currentPlan.name}
        price={currentPlan.price}
        pausedAt={props.billingPausedAt}
        onUpdateCard={props.onUpdateCard}
        isUpdateCardAvailable={Boolean(props.isUpdateCardAvailable)}
        isAwaitingUpdate={Boolean(props.isAwaitingUpdate)}
      />
    )
  }

  return null
}

function PlanCards({ account, plans, currentPlanId, recommendedPlanId, priceSuffix, onSelectPlan }: PlansContentProps) {
  return (
    <div className={classes.cards}>
      {plans.map((plan) => {
        const isCurrentPlan = !account.isFreeTrial && plan.paddle_id === currentPlanId

        return (
          <PlanOfferCard
            key={plan.paddle_id}
            plan={plan}
            priceSuffix={priceSuffix}
            buttonLabelId={getPlanButtonLabelId({ ...account, isCurrentPlan })}
            isRecommended={plan.paddle_id === recommendedPlanId}
            isCurrentPlan={isCurrentPlan}
            onSelect={onSelectPlan}
          />
        )
      })}
    </div>
  )
}
